import React, { useCallback, useMemo, useState } from 'react';
import { useStyletron } from "baseui";
import {
    translate,
    useEvent,
    useLazyQuery,
    uuidv4
} from "@soap/modules";
import { KIND } from "baseui/button";
import { TagSelect } from "../../../z50-domaincomp/tag-select";
import { SoapPaginator, useSoapPaginator } from "../../../z50-domaincomp/soap-paginator";
import { LinkButton } from "../../../z40-uicomp/buttons";
import { IncludeArchivedCheckbox } from "../../../z40-uicomp/include-archived-checkbox";
import { KIND as TAGKIND, Tag } from "baseui/tag";
import { useDeviceType } from "../../../hooks/useDeviceType";
import { useUserRole } from "../../../hooks/useUserRole";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Badge } from "../../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../ui/dropdown-menu";
import { MoreHorizontal, Plus, Tag as TagIcon, ArrowLeft, Check, Clock, Edit } from "lucide-react";
import { ArchiveStudentDialog } from "../dialogs/ArchiveStudentDialog";
import { DeleteStudentDialog } from "../dialogs/DeleteStudentDialog";
import { DownloadStudentDataDialog } from "../dialogs/DownloadStudentDataDialog";
import { AssignSubAccountDialog } from "../dialogs/AssignSubAccountDialog";
import { RevokeInviteDialog } from "../dialogs/RevokeInviteDialog";
import { RemoveStudentLoginDialog } from "../dialogs/RemoveStudentLoginDialog";
import { TagsManagementCard } from "./TagsManagementCard";

export function StudentTable() {
    const [css, theme] = useStyletron();
    const pageSize = 5;
    const { isTutor, isTeacher } = useUserRole();

    const {
        fetchPage,
        currentPageRecords,
        resetRecordSet,
        recordCount,
        currentPage
    } = useSoapPaginator("E131v1_GotStudents", "C158v1_GetStudents", pageSize, {});

    const [refreshTags, e103] = useLazyQuery("C119v1_GetTags", {
        C119_TagTypes: {
            SelectedKeys: ['student']
        }
    });

    const [getProfile, gotProfile] = useLazyQuery("C166v1_GetUserProfile", {});
    const [getOrg, gotOrg] = useLazyQuery("C241v1_GetOrganisationDetails");
    const { isOrgUser, requestingUserIsOrgAdmin } = useUserRole();

    const { isDesktop } = useDeviceType();
    
    // Only fetch org details if user has organization access
    const hasOrgAccess = isOrgUser || requestingUserIsOrgAdmin;

    // Fetch profile and org details on mount
    React.useEffect(() => {
        getProfile({});
        if (hasOrgAccess) {
            getOrg({});
        }
        fetchPage(); // Initial fetch for students table
        // Note: getProfile, getOrg, and fetchPage are intentionally excluded from dependencies as they should be stable
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasOrgAccess]);

    useEvent({
        eventName: "E135v1_StudentUpdated",
        onEventReceived: (event) => {
            setIsLoading(true);
            fetchPage();
        }
    }, [fetchPage]);

    useEvent({
        eventName: "E136v1_StudentArchived",
        onEventReceived: (e, v) => {
            setIsLoading(true);
            fetchPage();
        }
    }, [fetchPage]);

    useEvent({
        eventName: "E183v1_StudentDeleted",
        onEventReceived: (e, v) => {
            setIsLoading(true);
            fetchPage();
        }
    }, [fetchPage]);

    useEvent({
        eventName: "E108v1_TagUpdated",
        onEventReceived: (e, v) => {
            setIsLoading(true);
            fetchPage();
            refreshTags();
        }
    }, [fetchPage, refreshTags]);

    useEvent({
        eventName: "E106v1_TagRemoved",
        onEventReceived: (e, v) => {
            setIsLoading(true);
            fetchPage();
            refreshTags();
        }
    }, [fetchPage, refreshTags]);

    useEvent({
        eventName: "E105v1_TagAdded",
        onEventReceived: (e, v) => {
            refreshTags();
        }
    }, [refreshTags]);

    useEvent({
        eventName: "E202v1_StudentInviteSent",
        onEventReceived: (e, v) => {
            setIsLoading(true);
            fetchPage();
            if (hasOrgAccess) {
                getOrg({});
            }
        }
    }, [fetchPage, hasOrgAccess, getOrg]);

    useEvent({
        eventName: "E200v1_InvitationRevoked",
        onEventReceived: (e, v) => {
            setIsLoading(true);
            fetchPage();
            if (hasOrgAccess) {
                getOrg({});
            }
        }
    }, [fetchPage, hasOrgAccess, getOrg]);

    useEvent({
        eventName: "E197v1_OrganisationMembersUpdated",
        onEventReceived: (e, v) => {
            setIsLoading(true);
            fetchPage();
        }
    }, [fetchPage]);

    useEvent({
        eventName: "E131v1_GotStudents",
        onEventReceived: (event) => {
            if (event && event.e131_Students) {
                setIsLoading(false);
            }
        }
    });

    const [includeArchived, setIncludeArchived] = useState(false);
    const [selectedTags, setSelectedTags] = useState([]);
    const [tableKey, setTableKey] = useState(uuidv4());
    const [isFlipped, setIsFlipped] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const selectOptions = useMemo(() => e103?.e103_Tags.map(x => ({
        label: x.e103_Title,
        id: x.e103_LongId
    })) ?? [], [e103]);

    const onOptionsChange = useCallback((selectedOptions) => {
        setIsLoading(true);
        if (selectedOptions.length) {
            fetchPage(1, {
                C158_PipeSeparatedTagIds: selectedOptions.map(t => t.id).join('|'),
                C158_IncludeArchived: includeArchived
            });
        } else {
            resetRecordSet()
        }
        setSelectedTags(selectedOptions);
        setTableKey(uuidv4());
    }, [fetchPage, includeArchived, resetRecordSet]);

    const students = currentPageRecords?.e131_Students || [];

    return (
        <div className="space-y-4">
            {/* Header with controls - Flippable Card */}
            <Card className={`relative transition-all duration-500 ${isFlipped ? 'h-auto' : 'min-h-[120px]'}`}>
                <div className={`transition-transform duration-500 transform-style-preserve-3d ${isFlipped ? 'rotate-y-180' : ''}`}>
                    {/* Front side - current content */}
                    <div className={`${isFlipped ? 'hidden' : 'block'} backface-hidden`}>
                        <CardHeader>
                            <div className="flex flex-col gap-2">
                                {/* Tag controls row */}
                                <div className="flex flex-wrap items-center gap-2">
                                    <div className="flex-1">
                                        <TagSelect 
                                            options={selectOptions} 
                                            value={selectedTags} 
                                            onChange={onOptionsChange}
                                        />
                                    </div>
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => setIsFlipped(!isFlipped)}
                                    >
                                        <TagIcon className="w-4 h-4 mr-2" />
                                        {translate("Tags")}
                                    </Button>
                                </div>
                                
                                {/* Other controls row */}
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <IncludeArchivedCheckbox 
                                        archived={includeArchived} 
                                        archivedCount={!includeArchived ? (currentPageRecords?.e131_ArchivedCount ?? 0) : undefined}
                                        onChange={(includeArchived) => {
                                            setIsLoading(true);
                                            resetRecordSet({
                                                C158_PipeSeparatedTagIds: selectedTags.map(t => t.id).join('|'),
                                                C158_IncludeArchived: includeArchived
                                            });
                                            setIncludeArchived(includeArchived);
                                        }}
                                    />
                                    {/* Show Add Student button for tutors and teachers */}
                                    {(isTutor || isTeacher) && (
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={() => location.href = "#/app/profiles/students/add"}
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            {translate("Add Student")}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </CardHeader>
                    </div>
                    
                    {/* Back side - tags table */}
                    <div className={`${isFlipped ? 'block' : 'hidden'} rotate-y-180 backface-hidden bg-white rounded-lg border`}>
                        <CardHeader className="pb-0">
                            <div className="flex items-center justify-between">
                                <CardTitle>{translate("Student Tags")}</CardTitle>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => setIsFlipped(false)}
                                >
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                    Back to Students
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="pt-2">
                            <TagsManagementCard tagType="student" />
                        </CardContent>
                    </div>
                </div>
            </Card>

            {/* Students Table */}
            {!isFlipped && (
                <Card className="overflow-visible">
                    <CardContent className="p-0">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="flex flex-col items-center gap-3">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-blue"></div>
                                    <span className="text-gray-600">Loading students...</span>
                                </div>
                            </div>
                        ) : students.length > 0 ? (
                            <div className="overflow-x-auto overflow-y-visible">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Email</TableHead>
                                            <TableHead className="w-[100px]">Can Login</TableHead>
                                            {isDesktop && <TableHead>Tags</TableHead>}
                                            <TableHead className="w-[50px] sticky right-0 bg-white z-10">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {students.map((student) => (
                                            <TableRow 
                                                key={student.e131_StudentId} 
                                                className={`group hover:bg-gray-100 ${student.e131_Archived ? 'opacity-60 bg-gray-50' : ''}`}
                                            >
                                                <TableCell className={`font-medium ${student.e131_Archived ? 'text-gray-500 italic' : ''}`}>
                                                    {student.e131_Name}
                                                    {student.e131_Archived && <span className="ml-2 text-xs text-gray-400">(Archived)</span>}
                                                </TableCell>
                                                <TableCell className={student.e131_Archived ? 'text-gray-500' : ''}>
                                                    {student.e131_Email}
                                                </TableCell>
                                                <TableCell>
                                                    {student.e131_HasOwnAccount ? (
                                                        <Check className="w-5 h-5 text-confirm-action" />
                                                    ) : student.e131_HasPendingInvite ? (
                                                        <Clock className="w-5 h-5 text-amber-500" />
                                                    ) : (
                                                        <span className="text-gray-400">—</span>
                                                    )}
                                                </TableCell>
                                                {isDesktop && (
                                                    <TableCell>
                                                        <div className="flex flex-wrap gap-1">
                                                            {student.e131_TagsApplied?.map((tag, index) => (
                                                                <Badge key={index} variant="secondary" className="text-xs">
                                                                    {tag}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </TableCell>
                                                )}
                                                <TableCell className="sticky right-0 bg-white z-10">
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="sm">
                                                                <MoreHorizontal className="w-4 h-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            {student.e131_Archived !== true && (
                                                                <>
                                                                    <DropdownMenuItem
                                                                        className="w-full justify-start"
                                                                        onClick={() => location.href = `#/app/profiles/students/edit/${student.e131_StudentId}`}
                                                                    >
                                                                        <Edit className="w-4 h-4 mr-2" />
                                                                        Edit
                                                                    </DropdownMenuItem>
                                                                    {gotProfile?.e138_IsOnAPlanWithSubAccounts &&
                                                                     gotOrg?.e193_AvailableSubAccounts > 0 &&
                                                                     !student.e131_HasOwnAccount &&
                                                                     student.e131_Email && (
                                                                        student.e131_HasPendingInvite ? (
                                                                            <RevokeInviteDialog
                                                                                studentId={student.e131_StudentId}
                                                                                studentEmail={student.e131_Email}
                                                                            />
                                                                        ) : (
                                                                            <AssignSubAccountDialog
                                                                                studentId={student.e131_StudentId}
                                                                                studentName={student.e131_Name}
                                                                                studentEmail={student.e131_Email}
                                                                            />
                                                                        )
                                                                    )}
                                                                    {student.e131_HasOwnAccount && (
                                                                        <RemoveStudentLoginDialog
                                                                            studentId={student.e131_StudentId}
                                                                            studentName={student.e131_Name}
                                                                        />
                                                                    )}
                                                                    <ArchiveStudentDialog
                                                                        studentId={student.e131_StudentId}
                                                                        studentName={student.e131_Name}
                                                                    />
                                                                </>
                                                            )}
                                                            <DeleteStudentDialog
                                                                studentId={student.e131_StudentId}
                                                                studentName={student.e131_Name}
                                                            />
                                                            <DownloadStudentDataDialog studentId={student.e131_StudentId} />
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="p-8 text-center text-gray-500">
                                No students found
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Pagination */}
            {!isFlipped && students.length > 0 && (
                <SoapPaginator 
                    pageSize={pageSize}
                    page={currentPage}
                    recordCount={recordCount}
                    onPageChanged={(page) => {
                        setIsLoading(true);
                        fetchPage(page);
                    }}
                />
            )}
        </div>
    );
}
